import { ChevronDownIcon } from 'components/icons';
import type { TabType } from 'components/TabsGallery';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import type { KeyboardEvent, RefObject } from 'react';
import { FormattedMessage } from 'react-intl';

const TRIGGER_SLOT = 'dropdown-menu-trigger';

interface Props {
    tabs: TabType[];
    /** Index in `tabs` of the open tab, or -1 while the open tab sits in the bar. */
    activeIndex: number;
    onSelect: (index: number) => void;
    /** `byKeys`: opened from the tab's keys, which leaves the tab drawn as focused while focus is in the menu. */
    onOpenChange: (open: boolean, byKeys: boolean) => void;
    /** The tab this menu sits in: where focus goes back to as the menu closes. */
    tabRef: RefObject<HTMLElement | null>;
}

/**
 * The More tab's keys. The menu's trigger is a span inside the tab, which the keyboard cannot reach, so Enter, Space
 * and ↓ on the tab press it: a scripted click opens the menu as a key does, with focus on its first row. Space
 * acts as it is released, as on any button: acted on when pressed, its release would land on that row and pick
 * it. Keys from the open menu bubble here through React's tree and are left alone.
 */
export function pressMoreTabMenu(event: KeyboardEvent<HTMLElement>) {
    const opens = event.type === 'keyup' ? event.key === ' ' : event.key === 'Enter' || event.key === 'ArrowDown';
    if (!opens || event.target !== event.currentTarget) return;
    event.preventDefault();
    event.currentTarget.querySelector<HTMLElement>(`[data-slot="${TRIGGER_SLOT}"]`)?.click();
}

/**
 * The More tab of a tab strip: a menu of the tabs that did not make the bar. While one of them is open
 * the trigger names it and the menu checks it; `NavigationMenu`, the classic dropdown tab, can do neither.
 * The tab is the menu's button for the keyboard: {@link pressMoreTabMenu} opens it, and Esc or a choice
 * returns focus to the tab.
 */
export default function MoreTabMenu({ tabs, activeIndex, onSelect, onOpenChange, tabRef }: Props) {
    const active = tabs[activeIndex];

    return (
        // A scripted click, which is what the tab's keys make, counts no press: `detail` is 0.
        <DropdownMenu
            onOpenChange={(open, { event }) => onOpenChange(open, open && 'detail' in event && event.detail === 0)}
        >
            {/* A span, not a button: the trigger sits inside the tab's own <button>, as in NavigationMenu.
                self-stretch, not h-full: that button's height is only a min-height, so h-full resolves to auto
                and leaves the tab outside one text line dead to clicks. */}
            <DropdownMenuTrigger
                nativeButton={false}
                render={<span />}
                className="group/more flex items-center gap-1 self-stretch px-3"
            >
                {active ? (
                    <FormattedMessage id="tabs.more-active" values={{ tab: active.label }} />
                ) : (
                    <FormattedMessage id="tabs.more" />
                )}
                <ChevronDownIcon className="size-3.5 group-data-popup-open/more:rotate-180 motion-safe:transition-[rotate] motion-safe:duration-150 motion-safe:ease-out" />
            </DropdownMenuTrigger>
            {/* The trigger is not focusable, so the menu is told where to put focus back. */}
            <DropdownMenuContent className="w-56" finalFocus={tabRef}>
                <DropdownMenuRadioGroup value={activeIndex} onValueChange={onSelect}>
                    {tabs.map((tab, index) => (
                        <DropdownMenuRadioItem
                            key={tab.id}
                            value={index}
                            closeOnClick
                            className="data-checked:bg-primary-wash [&_[data-slot=dropdown-menu-radio-item-indicator]]:text-primary h-[34px] px-2.5 text-sm data-checked:font-semibold"
                        >
                            {tab.label}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
